/**
 * Cash Flow API Service
 *
 * Thin wrapper around the centralized axios instance for the cash flow
 * report endpoints. All calls go through src/auth/apiClient.ts.
 */

import api from '../../auth/apiClient';
import { CASH_FLOW_API_CONFIG, CASH_FLOW_DEFAULTS } from '../apiConfig';
import {
    ApiEnvelope,
    CashFlowDetailRow,
    CashFlowSummary,
    CashFlowTrendPoint,
    CashFlowWeeklyRow,
    CashFlowFilters,
} from '../types';

const baseParams = (filters: CashFlowFilters) => ({
    company_id: filters.company_id,
    year: filters.year,
    month: filters.month,
});

export class CashFlowApiService {
    static async getDetail(filters: CashFlowFilters): Promise<ApiEnvelope<CashFlowDetailRow[]>> {
        const response = await api.get<ApiEnvelope<CashFlowDetailRow[]>>(
            CASH_FLOW_API_CONFIG.endpoints.DETAIL,
            { params: baseParams(filters) },
        );
        return response.data;
    }

    static async getSummary(filters: CashFlowFilters): Promise<ApiEnvelope<CashFlowSummary[]>> {
        const response = await api.get<ApiEnvelope<CashFlowSummary[]>>(
            CASH_FLOW_API_CONFIG.endpoints.SUMMARY,
            { params: baseParams(filters) },
        );
        return response.data;
    }

    static async getTrend(filters: CashFlowFilters): Promise<ApiEnvelope<CashFlowTrendPoint[]>> {
        const response = await api.get<ApiEnvelope<CashFlowTrendPoint[]>>(
            CASH_FLOW_API_CONFIG.endpoints.TREND,
            {
                params: {
                    ...baseParams(filters),
                    months: filters.months ?? CASH_FLOW_DEFAULTS.TREND_MONTHS,
                },
            },
        );
        return response.data;
    }

    static async getWeekly(filters: CashFlowFilters): Promise<ApiEnvelope<CashFlowWeeklyRow[]>> {
        const response = await api.get<ApiEnvelope<CashFlowWeeklyRow[]>>(
            CASH_FLOW_API_CONFIG.endpoints.WEEKLY,
            { params: baseParams(filters) },
        );
        return response.data;
    }
}

export default CashFlowApiService;
